/**
 * Écran « Barème de TVA » : taux par catégorie et date d'effet. Les lignes par défaut sont en lecture seule ;
 * l'entreprise AJOUTE des changements de taux datés (jamais de modification des factures déjà émises).
 */
import { esc, fmtDate } from '@presentation/shell/format';
import {
  BaremeTvaInvalideError,
  type CategorieTva,
  type TauxTvaDate,
} from '@domain/fiscalite/BaremeTva';
import {
  AIDE_BAREME_TVA,
  LIBELLE_CATEGORIE_TVA,
  ORDRE_CATEGORIES_TVA,
  ajouterChangementDeTaux,
  construireLignesBareme,
  extraireAjouts,
  retirerAjout,
  texteAttenteExpert,
} from '@application/comptabilite/EcranBaremeTva';
import {
  STYLE_AIDE,
  STYLE_SCROLL,
  STYLE_TH_LIGNE,
  bandeauExpertHtml,
  erreurChampHtml,
  majErreurChamp,
  majResume,
  majStatut,
  type Confirmer,
  type Notifier,
} from './panelKit';

export interface BaremeTvaDeps {
  canMutate: boolean;
  /** Barème complet résolu (défauts + ajouts de l'entreprise). */
  load(): Promise<readonly TauxTvaDate[]>;
  /** Enregistre les lignes ajoutées par l'entreprise (taux en fraction). */
  save(ajouts: readonly TauxTvaDate[]): Promise<readonly TauxTvaDate[]>;
  audit(description: string, payload?: Record<string, unknown>): Promise<void>;
  confirm: Confirmer;
  notify: Notifier;
}

const P = 'cpt-bareme';

export async function mountBaremeTvaPanel(host: HTMLElement, deps: BaremeTvaDeps): Promise<void> {
  let bareme = await deps.load();
  let formulaireOuvert = false;

  const render = (): void => {
    const lignes = construireLignesBareme(bareme);
    const attente = texteAttenteExpert(bareme);
    const corps = lignes
      .map((l) => {
        const suppr =
          deps.canMutate && l.supprimable
            ? `<button type="button" class="btn btn-danger btn-sm" data-supprimer="${l.categorie}" data-depuis="${esc(l.depuis)}"
                 aria-label="Supprimer le changement de taux ${esc(l.categorieLibelle)} du ${esc(fmtDate(l.depuis))}">Supprimer</button>`
            : l.parDefaut
              ? '<span style="font-size:12px;color:var(--ardoise);">Valeur du logiciel</span>'
              : '';
        const statut =
          l.statut === 'a_confirmer'
            ? `<span class="pill warning"><span aria-hidden="true">⚠ </span>${esc(l.statutLibelle)}</span>`
            : `<span class="pill info">${esc(l.statutLibelle)}</span>`;
        return `<tr>
          <th scope="row" style="${STYLE_TH_LIGNE}">${esc(l.categorieLibelle)}</th>
          <td class="num">${esc(l.tauxPourcent)} %</td>
          <td>${l.depuis === '1970-01-01' ? 'Depuis toujours' : esc(fmtDate(l.depuis))}</td>
          <td>${statut}</td>
          <td style="max-width:360px;font-size:12px;">${esc(l.source)}</td>
          <td>${suppr}</td>
        </tr>`;
      })
      .join('');

    const options = ORDRE_CATEGORIES_TVA.map(
      (c) => `<option value="${c}">${esc(LIBELLE_CATEGORIE_TVA[c])}</option>`,
    ).join('');

    host.innerHTML = `
      <div class="card" id="${P}-panel" aria-labelledby="${P}-titre">
        <h2 class="card-title" id="${P}-titre">Barème de TVA</h2>
        ${bandeauExpertHtml()}
        <p style="${STYLE_AIDE}">${esc(AIDE_BAREME_TVA)}
          ${deps.canMutate ? '' : '<strong>Lecture seule : seuls l’administrateur, le gestionnaire RH et le comptable peuvent ajouter un changement de taux.</strong>'}</p>
        ${attente ? `<p id="${P}-attente" style="${STYLE_AIDE}font-weight:700;">${esc(attente)}</p>` : ''}
        <div style="${STYLE_SCROLL}">
          <table class="data" id="${P}-table">
            <caption class="sr-only">Barème de TVA : taux par catégorie et date d'entrée en vigueur</caption>
            <thead><tr><th scope="col">Catégorie</th><th scope="col" class="num">Taux</th><th scope="col">En vigueur depuis</th><th scope="col">Statut</th><th scope="col">Source</th><th scope="col"><span class="sr-only">Action</span></th></tr></thead>
            <tbody>${corps}</tbody>
          </table>
        </div>
        <p id="${P}-statut" role="status" style="${STYLE_AIDE}margin-top:8px;"></p>
        ${
          deps.canMutate
            ? `<div style="margin-top:8px;">
                 <button type="button" class="btn btn-secondary" id="${P}-ouvrir" aria-expanded="${formulaireOuvert}" aria-controls="${P}-formulaire">Ajouter un changement de taux</button>
               </div>
               <form id="${P}-formulaire" novalidate ${formulaireOuvert ? '' : 'hidden'} aria-labelledby="${P}-form-titre" style="margin-top:12px;max-width:640px;">
                 <h3 id="${P}-form-titre" style="font-size:14px;color:var(--marine);margin:0 0 8px;">Nouveau changement de taux</h3>
                 <div id="${P}-resume" class="form-error-summary" role="alert" hidden></div>
                 <div class="form-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
                   <div class="field"><label for="${P}-categorie">Catégorie</label>
                     <select id="${P}-categorie" aria-describedby="${P}-err-categorie">${options}</select>${erreurChampHtml(`${P}-err-categorie`, undefined)}</div>
                   <div class="field"><label for="${P}-taux">Nouveau taux (%)</label>
                     <input id="${P}-taux" type="text" inputmode="decimal" autocomplete="off" placeholder="exemple : 18" aria-describedby="${P}-err-taux">${erreurChampHtml(`${P}-err-taux`, undefined)}</div>
                   <div class="field"><label for="${P}-depuis">Date d'effet</label>
                     <input id="${P}-depuis" type="date" aria-describedby="${P}-err-depuis">${erreurChampHtml(`${P}-err-depuis`, undefined)}</div>
                   <div class="field"><label for="${P}-commentaire">Commentaire (texte de loi, décision…)</label>
                     <input id="${P}-commentaire" type="text" autocomplete="off"></div>
                 </div>
                 <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:12px;">
                   <button type="submit" class="btn btn-primary" id="${P}-ajouter">Ajouter ce changement</button>
                   <button type="button" class="btn btn-ghost" id="${P}-annuler">Annuler</button>
                 </div>
               </form>`
            : ''
        }
      </div>`;
    bind();
  };

  const champ = (id: string): HTMLInputElement | HTMLSelectElement | null =>
    host.querySelector<HTMLInputElement | HTMLSelectElement>(`#${P}-${id}`);

  const bind = (): void => {
    const form = host.querySelector<HTMLFormElement>(`#${P}-formulaire`);
    const ouvrir = host.querySelector<HTMLButtonElement>(`#${P}-ouvrir`);
    ouvrir?.addEventListener('click', () => {
      formulaireOuvert = true;
      if (form) form.hidden = false;
      ouvrir.setAttribute('aria-expanded', 'true');
      champ('categorie')?.focus();
    });
    host.querySelector(`#${P}-annuler`)?.addEventListener('click', () => {
      formulaireOuvert = false;
      render();
      host.querySelector<HTMLButtonElement>(`#${P}-ouvrir`)?.focus();
    });
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      void ajouter();
    });
    host.querySelectorAll<HTMLButtonElement>('[data-supprimer]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void supprimer(btn.dataset['supprimer'] as CategorieTva, btn.dataset['depuis'] ?? '');
      });
    });
  };

  const ajouter = async (): Promise<void> => {
    if (!deps.canMutate) return;
    const resume = host.querySelector<HTMLElement>(`#${P}-resume`);
    majResume(resume, null);
    const r = ajouterChangementDeTaux(extraireAjouts(bareme), {
      categorie: (champ('categorie')?.value ?? 'normal') as CategorieTva,
      tauxPourcent: champ('taux')?.value ?? '',
      depuis: champ('depuis')?.value ?? '',
      commentaire: champ('commentaire')?.value ?? '',
    });
    const erreurs = r.ok ? {} : r.erreurs;
    majErreurChamp(host, champ('categorie'), `${P}-err-categorie`, erreurs.categorie);
    majErreurChamp(host, champ('taux'), `${P}-err-taux`, erreurs.taux);
    majErreurChamp(host, champ('depuis'), `${P}-err-depuis`, erreurs.depuis);
    if (!r.ok) {
      if (erreurs.general) majResume(resume, erreurs.general);
      const premier = (['categorie', 'taux', 'depuis'] as const).find((k) => erreurs[k]);
      if (premier) champ(premier)?.focus();
      return;
    }
    try {
      bareme = await deps.save(r.ajouts);
    } catch (err) {
      if (err instanceof BaremeTvaInvalideError) {
        majResume(resume, err.message);
        return;
      }
      deps.notify.error(err instanceof Error ? err.message : 'Enregistrement impossible.');
      return;
    }
    const ajoutee = r.ajouts[r.ajouts.length - 1];
    await deps.audit('Barème de TVA : ajout d’un changement de taux', { ligne: ajoutee });
    formulaireOuvert = false;
    render();
    host.querySelector<HTMLElement>(`#${P}-ouvrir`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`), 'Changement de taux ajouté au barème.');
    deps.notify.success('Changement de taux ajouté. Les factures déjà émises ne changent pas.');
  };

  const supprimer = async (categorie: CategorieTva, depuis: string): Promise<void> => {
    if (!deps.canMutate) return;
    const ok = await deps.confirm({
      title: 'Supprimer ce changement de taux',
      message: `Le changement de taux « ${LIBELLE_CATEGORIE_TVA[categorie]} » du ${fmtDate(depuis)} sera retiré du barème. Les factures déjà émises ne changent pas. Continuer ?`,
      confirmLabel: 'Supprimer',
      danger: true,
      host,
    });
    if (!ok) return;
    const ajouts = retirerAjout(extraireAjouts(bareme), categorie, depuis);
    try {
      bareme = await deps.save(ajouts);
    } catch (err) {
      deps.notify.error(err instanceof Error ? err.message : 'Suppression impossible.');
      return;
    }
    await deps.audit('Barème de TVA : suppression d’un changement de taux', { categorie, depuis });
    render();
    host.querySelector<HTMLElement>(`#${P}-ouvrir`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`), 'Changement de taux supprimé.');
    deps.notify.success('Changement de taux supprimé.');
  };

  render();
}
